// RequireCap.tsx — route-level capability guard.
// Wraps a route's element and checks the user's active role has the required
// capability. If not, renders an "access denied" placeholder instead.
import type { ReactNode } from 'react';
import { useStore } from '@/store/store';
import { Icon } from '@/foundation/icons';

interface RequireCapProps {
  /** The capability string to check (e.g. 'master.access') */
  cap: string;
  children: ReactNode;
}

export function RequireCap({ cap, children }: RequireCapProps) {
  const { can } = useStore();

  if (can(cap)) {
    return <>{children}</>;
  }

  return <AccessDeniedPanel />;
}

/** The denied visual on its own — also rendered by pages whose API calls come
 *  back 403 (server-side grants can diverge from the static capability map). */
export function AccessDeniedPanel() {
  const { roleName } = useStore();

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--space-4)',
        padding: '80px 32px',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          background: 'var(--error-soft)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon name="lock" size={28} color="var(--error-dark)" />
      </div>
      <h2
        style={{
          fontSize: 'var(--fs-display)',
          fontWeight: 700,
          color: 'var(--text-primary)',
          margin: 0,
        }}
      >
        Access Denied
      </h2>
      <p
        style={{
          fontSize: 'var(--fs-emphasis)',
          color: 'var(--text-secondary)',
          margin: 0,
          maxWidth: 400,
        }}
      >
        Your current role ({roleName}) does not have the required permission to access this page.
        Contact your System Administrator if you need access.
      </p>
    </div>
  );
}
